import { useState, useEffect, useCallback, useRef } from "react";
import { SOLAR } from "../data/solarData";

export default function useAppState() {
  const [cur, setCur]             = useState(0);
  const [, setBusy]               = useState(false);
  const busyRef                   = useRef(false);
  const [hintDone, setHintDone]   = useState(false);
  const [loadPct, setLoadPct]     = useState(0);
  const [loadName, setLoadName]   = useState("Mapping the universe…");
  const [loaded, setLoaded]       = useState(false);
  const [panelShow, setPanelShow] = useState(false);
  const [panelExit, setPanelExit] = useState(false);
  const [cardsShow, setCardsShow] = useState(false);
  const [navShow, setNavShow]     = useState(false);
  const [navOpen, setNavOpen]     = useState(false);
  const [flash, setFlash]         = useState(false);
  const [textures, setTextures]   = useState({});
  const [navTextures, setNavTex]  = useState({});
  const [lines, setLines]         = useState({ tl:{}, tr:{}, bl:{}, br:{} });

  const isMobile  = () => window.innerWidth <= 700;
  const isConnect = cur === SOLAR.length - 1;
  const panelData = isConnect ? null : SOLAR[cur];

  // ── stage-connect class on html + body ───────────────────────────────────
  useEffect(() => {
    const method = isConnect ? "add" : "remove";
    document.documentElement.classList[method]("stage-connect");
    document.body.classList[method]("stage-connect");
  }, [isConnect]);

  // ── Mobile scrolling on connect screen ───────────────────────────────────
  useEffect(() => {
    if (!isMobile()) return;
    const stage = document.getElementById("stage");
    if (isConnect) {
      document.documentElement.style.touchAction = "pan-y";
      document.body.style.touchAction = "pan-y";
      document.body.style.overflow = "hidden";
      if (stage) { stage.style.overflowY = "auto"; stage.style.overflowX = "hidden"; }
    } else {
      document.documentElement.style.touchAction = "none";
      document.body.style.touchAction = "none";
      if (stage) { stage.style.overflowY = ""; stage.style.overflowX = ""; }
    }
  }, [isConnect]);

  // ── Arrow recalculation ───────────────────────────────────────────────────
  const updateArrows = useCallback(() => {
    const NAV_W  = isMobile() ? 0 : 230;
    const stageW = window.innerWidth - NAV_W;
    const cx     = NAV_W + stageW / 2;
    const cy     = window.innerHeight / 2 - window.innerHeight * 0.05;
    const r      = Math.min(stageW, window.innerHeight) * 0.28;
    const pts = {
      tl: { px: cx - r * .72, py: cy - r * .72 },
      tr: { px: cx + r * .72, py: cy - r * .72 },
      bl: { px: cx - r * .72, py: cy + r * .55 },
      br: { px: cx + r * .72, py: cy + r * .55 },
    };
    ["tl", "tr", "bl", "br"].forEach(id => {
      const dot = document.getElementById(`dot-${id}`);
      if (!dot) return;
      const db = dot.getBoundingClientRect();
      setLines(prev => ({
        ...prev,
        [id]: { x1: db.left + db.width / 2, y1: db.top + db.height / 2, x2: pts[id].px, y2: pts[id].py },
      }));
    });
  }, []);

  useEffect(() => {
    if (cardsShow && !isConnect) {
      const t1 = setTimeout(updateArrows, 50);
      const t2 = setTimeout(updateArrows, 200);
      return () => {
        clearTimeout(t1);
        clearTimeout(t2);
      };
    }
  }, [cur, cardsShow, isConnect, updateArrows]);

  useEffect(() => {
    window.addEventListener("resize", updateArrows);
    return () => window.removeEventListener("resize", updateArrows);
  }, [updateArrows]);

  // ── Loader / texture builder ──────────────────────────────────────────────
  useEffect(() => {
    const planetsWithTex = SOLAR.filter((p) => p.texUrl);
    const total = planetsWithTex.length || 1;
    let loadedCount = 0;

    setLoadName("Mapping the universe…");

    const loadPromises = SOLAR.map((pd) => {
      if (!pd.texUrl) return Promise.resolve();

      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          const aspect = img.naturalWidth / img.naturalHeight || 2;
          setTextures((t) => ({ ...t, [pd.id]: { url: pd.texUrl, aspect } }));
          setNavTex((t) => ({ ...t, [pd.id]: { url: pd.texUrl, aspect } }));
          loadedCount++;
          setLoadPct(Math.round((loadedCount / total) * 100));
          setLoadName(`Loaded ${pd.name}…`);
          resolve();
        };
        img.onerror = () => {
          setTextures((t) => ({ ...t, [pd.id]: { url: pd.texUrl, aspect: 2 } }));
          setNavTex((t) => ({ ...t, [pd.id]: { url: pd.texUrl, aspect: 2 } }));
          loadedCount++;
          setLoadPct(Math.round((loadedCount / total) * 100));
          resolve();
        };
        img.src = pd.texUrl;
      });
    });

    Promise.all(loadPromises).then(() => {
      setLoadPct(100);
      setLoadName("Ready");
      setCur(0);
      setTimeout(() => {
        setLoaded(true);
        setTimeout(() => {
          setPanelShow(true);
          setCardsShow(true);
          setNavShow(true);
          setTimeout(updateArrows, 50);
        }, 200);
      }, 150);
    });
  }, []); // eslint-disable-line

  // ── Navigation ────────────────────────────────────────────────────────────
  const goTo = useCallback((i) => {
    if (i < 0 || i > SOLAR.length - 1 || busyRef.current || i === cur) return;
    busyRef.current = true;
    setHintDone(true);
    setBusy(true);

    // Phase 1: Fast exit of current content (120ms exit)
    setPanelExit(true);
    if (isMobile()) setNavOpen(false);

    // Phase 2: Update planet index & trigger simultaneous entrance animation
    setTimeout(() => {
      setCur(i);
      setPanelExit(false);
      setFlash(true);
      setTimeout(() => setFlash(false), 180);

      if (i === SOLAR.length - 1) {
        setPanelShow(false);
      } else {
        setPanelShow(true);
      }
    }, 120);

    // Phase 3: Unlock navigation lock quickly for snappy page switching
    setTimeout(() => {
      setBusy(false);
      busyRef.current = false;
    }, 600);
  }, [cur]);

  const handleToggleNav = (val) =>
    val !== undefined ? setNavOpen(val) : setNavOpen(o => !o);

  // ── Keyboard / wheel / touch ──────────────────────────────────────────────
  useEffect(() => {
    const onKey = e => {
      const active = document.activeElement;
      const target = e.target;
      const isInput =
        (active && (active.tagName === "INPUT" || active.tagName === "TEXTAREA" || active.tagName === "SELECT" || active.isContentEditable || active.closest("#stage-form") || active.closest("form"))) ||
        (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.tagName === "SELECT" || target.isContentEditable || target.closest("#stage-form") || target.closest("form")));

      if (isInput) return;

      if (e.key === "ArrowDown" || e.key === " ") { goTo(cur + 1); e.preventDefault(); }
      if (e.key === "ArrowUp")                     { goTo(cur - 1); e.preventDefault(); }
    };
    let ty0 = 0, tx0 = 0;
    const onTS = e => { ty0 = e.touches[0].clientY; tx0 = e.touches[0].clientX; };
    const onTE = e => {
      const tgt = document.elementFromPoint(tx0, ty0);
      if (tgt?.closest("#stage-form") &&
          (tgt.tagName === "INPUT" || tgt.tagName === "TEXTAREA" || tgt.isContentEditable)) return;
      const dy = ty0 - e.changedTouches[0].clientY;
      const dx = Math.abs(tx0 - e.changedTouches[0].clientX);
      if (Math.abs(dy) < 45 || dx > Math.abs(dy) * .85) return;
      dy > 0 ? goTo(cur + 1) : goTo(cur - 1);
    };
    const onWheel = e => {
      if (window._wheelLock) return; 
      window._wheelLock = true;
      e.deltaY > 0 ? goTo(cur + 1) : goTo(cur - 1);
      setTimeout(() => window._wheelLock = false, 1400);
    };
    document.addEventListener("keydown",    onKey);
    document.addEventListener("touchstart", onTS,    { passive: true });
    document.addEventListener("touchend",   onTE,    { passive: true });
    document.addEventListener("wheel",      onWheel, { passive: true });
    return () => {
      document.removeEventListener("keydown",    onKey);
      document.removeEventListener("touchstart", onTS);
      document.removeEventListener("touchend",   onTE);
      document.removeEventListener("wheel",      onWheel);
    };
  }, [cur, goTo, isConnect]);

  return {
    cur, loaded, loadPct, loadName,
    panelShow, panelExit, cardsShow,
    navShow, navOpen, flash, hintDone,
    textures, navTextures, lines,
    isConnect, panelData,
    goTo, handleToggleNav,
  };
}
